import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { CloudOutlined, RefreshOutlined, RouterOutlined, VideocamOutlined } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import OrganizationContentStack from '@/components/organization-content-stack';
import PageHeaderBar from '@/components/common/page-header-bar';
import EmptyState from '@/components/common/empty-state';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import {
  filterSingleCamAgentsByStatus,
  useListSingleCamAgentPoolQuery,
} from '@/state/queries/singlecam-agent-admin/use-list-singlecam-agent-pool';
import {
  useCreateAzureSingleCamAgentMutation,
  useCreateStandaloneSingleCamAgentMutation,
} from '@/state/mutations/singlecam-agent-admin/use-singlecam-agent-lifecycle-mutations';
import { usePairSingleCamAgentDeviceMutation } from '@/state/mutations/singlecam-agent-admin/use-pair-singlecam-agent-device';
import { useNotifications } from '@/hooks/use-notifications';
import SingleCamAgentLifecycleActions, {
  SingleCamAgentVmCell,
} from '@/components/singlecam-agent-admin/singlecam-agent-lifecycle-actions';
import AgentPairDeviceDialog from '@/components/agent-pools-admin/agent-pair-device-dialog';
import AgentPoolPlayerCell from '@/components/agent-pools-admin/agent-pool-player-cell';
import type { SingleCamAgentPoolMemberDto } from '@/types/dtos/singlecam-agent/singlecam-agent-pool.dto';

const STATUS_FILTERS = [
  { i18nKey: 'admin.singlecamAgentAdmin.status.all', fallback: 'All', value: '' },
  { i18nKey: 'admin.singlecamAgentAdmin.status.connected', fallback: 'Connected', value: 'connected' },
  { i18nKey: 'admin.singlecamAgentAdmin.status.offline', fallback: 'Offline', value: 'offline' },
  { i18nKey: 'admin.singlecamAgentAdmin.status.allocated', fallback: 'Allocated', value: 'allocated' },
  { i18nKey: 'admin.singlecamAgentAdmin.status.free', fallback: 'Free', value: 'free' },
  { i18nKey: 'admin.singlecamAgentAdmin.status.maintenance', fallback: 'Maintenance', value: 'maintenance' },
  { i18nKey: 'admin.singlecamAgentAdmin.status.paused', fallback: 'Paused', value: 'paused' },
] as const;

function getStatusLabel(
  t: ReturnType<typeof useTranslations>,
  status: 'connected' | 'offline' | 'maintenance' | 'paused',
): string {
  const filter = STATUS_FILTERS.find((entry) => entry.value === status);
  return filter ? t(filter.i18nKey, undefined, filter.fallback) : status;
}

function ConnectionChip({ agent }: { agent: SingleCamAgentPoolMemberDto }) {
  const t = useTranslations();
  const allocated = Boolean(agent.allocatedStagePlayerId);

  if (agent.opsStatus === 'maintenance') {
    return <Chip size="small" label={getStatusLabel(t, 'maintenance')} color="warning" variant="outlined" />;
  }
  if (agent.opsStatus === 'paused') {
    return <Chip size="small" label={getStatusLabel(t, 'paused')} color="default" variant="outlined" />;
  }
  if (agent.isConnected) {
    const label = allocated
      ? t('admin.singlecamAgentAdmin.status.allocatedConnected', undefined, 'Allocated · Connected')
      : getStatusLabel(t, 'connected');
    return <Chip size="small" label={label} color="success" variant="outlined" />;
  }
  const label = allocated
    ? t('admin.singlecamAgentAdmin.status.allocatedOffline', undefined, 'Allocated · Offline')
    : getStatusLabel(t, 'offline');
  return <Chip size="small" label={label} color={allocated ? 'warning' : 'default'} variant="outlined" />;
}

function DeploymentChip({ agent }: { agent: SingleCamAgentPoolMemberDto }) {
  if (agent.deployment === 'azure') {
    return <Chip size="small" label="Azure VM" icon={<CloudOutlined />} variant="outlined" />;
  }
  return <Chip size="small" label="Standalone" icon={<RouterOutlined />} variant="outlined" />;
}

async function copyText(value: string) {
  await navigator.clipboard.writeText(value);
}

type SingleCamAgentAdminPanelProps = {
  embedded?: boolean;
  hasAccess?: boolean;
  featureLoading?: boolean;
};

export default function SingleCamAgentAdminPanel({
  embedded = false,
  hasAccess: hasAccessProp,
  featureLoading: featureLoadingProp,
}: SingleCamAgentAdminPanelProps) {
  const t = useTranslations();
  const notification = useNotifications();
  const featureAccess = useFeatureAccess('singlecam-agent');
  const hasAccess = hasAccessProp ?? featureAccess.hasAccess;
  const featureLoading = featureLoadingProp ?? featureAccess.isLoading;
  const [statusFilter, setStatusFilter] = useState('');
  const [pairDialogPlayerId, setPairDialogPlayerId] = useState<string | null>(null);

  const {
    data: agents = [],
    isLoading,
    error,
    refetch,
    isFetching,
  } = useListSingleCamAgentPoolQuery({
    enabled: hasAccess,
  });
  const { mutateAsync: createAzure, isPending: isCreatingAzure } = useCreateAzureSingleCamAgentMutation();
  const { mutateAsync: createStandalone, isPending: isCreatingStandalone } =
    useCreateStandaloneSingleCamAgentMutation();
  const { mutateAsync: pairDevice, isPending: isPairing } = usePairSingleCamAgentDeviceMutation();

  const filtered = useMemo(() => filterSingleCamAgentsByStatus(agents, statusFilter), [agents, statusFilter]);
  const isCreating = isCreatingAzure || isCreatingStandalone;

  const handleCreateAzure = useCallback(async () => {
    try {
      await createAzure(undefined);
      notification.notifySuccess(
        t('admin.singlecamAgentAdmin.createAzureSuccess', undefined, 'VM-based single-cam agent created and deploying'),
      );
    } catch {
      notification.notifyError(
        t('admin.singlecamAgentAdmin.createAzureError', undefined, 'Failed to create VM-based single-cam agent'),
      );
    }
  }, [createAzure, notification, t]);

  const handleCreateStandalone = useCallback(async () => {
    try {
      const result = await createStandalone(undefined);
      setPairDialogPlayerId(result.playerId);
      notification.notifySuccess(
        t('admin.singlecamAgentAdmin.createStandaloneSuccess', undefined, 'Standalone agent created — pair with OTP'),
      );
    } catch {
      notification.notifyError(
        t('admin.singlecamAgentAdmin.createStandaloneError', undefined, 'Failed to create standalone agent'),
      );
    }
  }, [createStandalone, notification, t]);

  const pairDialogLabels = useMemo(
    () => ({
      title: t('admin.singlecamAgentAdmin.pairTitle', undefined, 'Pair standalone device'),
      pairingCodeHint: t(
        'admin.singlecamAgentAdmin.pairingCode',
        undefined,
        'Pairing code from Media Processor UI (select Single-Cam agent type)',
      ),
      pair: t('admin.singlecamAgentAdmin.pair', undefined, 'Pair device'),
      pairing: t('admin.singlecamAgentAdmin.pairing', undefined, 'Pairing…'),
      cancel: t('common.cancel', undefined, 'Cancel'),
      tokenHint: t(
        'admin.singlecamAgentAdmin.tokenHint',
        undefined,
        'VM token issued — copy for single-cam agent env if needed:',
      ),
      invalidCode: t('admin.singlecamAgentAdmin.invalidCode', undefined, 'Pairing code must be 6 digits'),
      pairSuccess: t('admin.singlecamAgentAdmin.pairSuccess', undefined, 'Agent paired'),
      pairError: t('admin.singlecamAgentAdmin.pairError', undefined, 'Pairing failed'),
      copied: t('common.copied', undefined, 'Copied'),
      copyToken: t('common.copyToken', undefined, 'Copy token'),
    }),
    [t],
  );

  if (featureLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasAccess) {
    const denied = (
      <Alert severity="warning" sx={{ m: embedded ? 0 : 2 }}>
        {t(
          'admin.singlecamAgentAdmin.featureRequired',
          undefined,
          'Single-cam agent feature is not enabled for this account.',
        )}
      </Alert>
    );
    if (embedded) return denied;
    return <OrganizationContentStack content={denied} />;
  }

  const toolbar = (
    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: embedded ? 1.5 : 0 }}>
      <Button
        size="small"
        variant="contained"
        startIcon={isCreatingAzure ? <CircularProgress size={16} color="inherit" /> : <CloudOutlined />}
        onClick={() => void handleCreateAzure()}
        disabled={isCreating}
      >
        {t('admin.singlecamAgentAdmin.createAzure', undefined, 'Create VM agent')}
      </Button>
      <Button
        size="small"
        variant="outlined"
        startIcon={isCreatingStandalone ? <CircularProgress size={16} color="inherit" /> : <RouterOutlined />}
        onClick={() => void handleCreateStandalone()}
        disabled={isCreating}
      >
        {t('admin.singlecamAgentAdmin.createStandalone', undefined, 'Create paired agent')}
      </Button>
      <Tooltip title={t('admin.singlecamAgentAdmin.refresh', undefined, 'Refresh')}>
        <span>
          <IconButton onClick={() => refetch()} disabled={isFetching} size="small">
            {isFetching ? <CircularProgress size={20} /> : <RefreshOutlined />}
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );

  const content = (
    <Box sx={{ height: embedded ? 'auto' : '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {!embedded && (
        <PageHeaderBar
          mainContent={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <VideocamOutlined sx={{ fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('admin.singlecamAgentAdmin.title', undefined, 'Single-Cam Agent Pool')}
                </Typography>
              </Box>
              {toolbar}
            </Box>
          }
        />
      )}
      {embedded && toolbar}

      <Alert severity="info" sx={{ mt: embedded ? 0 : 1.5 }}>
        {t(
          'admin.singlecamAgentAdmin.scaleHint',
          undefined,
          'Agents are allocated automatically to stage players when a flow goes live. Use force release when an allocation is stuck.',
        )}
      </Alert>

      <Stack direction="row" spacing={0.5} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
        {STATUS_FILTERS.map((filter) => (
          <Chip
            key={filter.value}
            label={t(filter.i18nKey, undefined, filter.fallback)}
            size="small"
            variant={statusFilter === filter.value ? 'filled' : 'outlined'}
            color={statusFilter === filter.value ? 'primary' : 'default'}
            onClick={() => setStatusFilter(filter.value)}
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Stack>

      <Card sx={{ mt: 1.5, flex: embedded ? undefined : 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <CardContent
          sx={{
            'flex': 1,
            'display': 'flex',
            'flexDirection': 'column',
            'minHeight': 0,
            'p': 0,
            '&:last-child': { pb: 0 },
          }}
        >
          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {t('admin.singlecamAgentAdmin.error', undefined, 'Error loading single-cam agent pool')}
            </Alert>
          )}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : filtered.length === 0 ? (
            <EmptyState
              text={
                statusFilter
                  ? t('admin.singlecamAgentAdmin.emptyFilter', undefined, 'No agents match this filter')
                  : t('admin.singlecamAgentAdmin.empty', undefined, 'No single-cam agents yet')
              }
            />
          ) : (
            <TableContainer sx={{ flex: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('admin.singlecamAgentAdmin.columns.name', undefined, 'Name')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('admin.singlecamAgentAdmin.columns.type', undefined, 'Type')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('admin.singlecamAgentAdmin.columns.status', undefined, 'Status')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('admin.singlecamAgentAdmin.columns.allocation', undefined, 'Allocation')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('admin.singlecamAgentAdmin.columns.agent', undefined, 'Agent')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('admin.singlecamAgentAdmin.columns.vm', undefined, 'VM')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('admin.singlecamAgentAdmin.columns.actions', undefined, 'Actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((agent) => (
                    <TableRow key={agent.playerId} hover>
                      <TableCell>
                        <AgentPoolPlayerCell
                          name={agent.name || 'Single-Cam Agent'}
                          playerId={agent.playerId}
                          pairTooltip={t('admin.singlecamAgentAdmin.pair', undefined, 'Pair device')}
                          showPair={agent.deployment === 'standalone' && !agent.isConnected}
                          onCopy={() =>
                            void copyText(agent.playerId).then(() =>
                              notification.notifySuccess(t('common.copied', undefined, 'Copied')),
                            )
                          }
                          onPair={() => setPairDialogPlayerId(agent.playerId)}
                        />
                      </TableCell>
                      <TableCell>
                        <DeploymentChip agent={agent} />
                      </TableCell>
                      <TableCell>
                        <ConnectionChip agent={agent} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {agent.allocatedStagePlayerId || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{agent.agentVersion || '—'}</TableCell>
                      <TableCell>
                        <SingleCamAgentVmCell agent={agent} />
                      </TableCell>
                      <TableCell>
                        <SingleCamAgentLifecycleActions agent={agent} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {pairDialogPlayerId && (
        <AgentPairDeviceDialog
          open
          playerId={pairDialogPlayerId}
          labels={pairDialogLabels}
          isPairing={isPairing}
          onClose={() => setPairDialogPlayerId(null)}
          onPair={pairDevice}
        />
      )}
    </Box>
  );

  if (embedded) return content;
  return <OrganizationContentStack content={content} />;
}
